import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  background: white;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  padding: var(--space-12) var(--space-4);
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: var(--text-4xl);
  color: var(--primary-900);
  margin-bottom: var(--space-4);
  font-family: var(--font-serif);

  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const PageSubtitle = styled.p`
  font-size: var(--text-xl);
  color: var(--neutral-700);
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

const ContentSection = styled.section`
  max-width: 800px;
  margin: 0 auto;
  padding: var(--space-12) var(--space-4);

  @media (max-width: 768px) {
    padding: var(--space-8) var(--space-4);
  }
`;

const LastUpdated = styled.p`
  font-size: var(--text-sm);
  color: var(--neutral-600);
  margin-bottom: var(--space-8);
  text-align: center;
  font-style: italic;
`;

const ContentBlock = styled.div`
  margin-bottom: var(--space-8);

  h2 {
    font-size: var(--text-2xl);
    color: var(--neutral-900);
    margin-bottom: var(--space-4);
    font-family: var(--font-serif);
  }

  h3 {
    font-size: var(--text-xl);
    color: var(--neutral-800);
    margin-bottom: var(--space-3);
    margin-top: var(--space-6);
  }

  p {
    font-size: var(--text-base);
    line-height: 1.8;
    color: var(--neutral-800);
    margin-bottom: var(--space-4);
  }

  ul {
    margin: var(--space-4) 0;
    padding-left: var(--space-6);

    li {
      font-size: var(--text-base);
      color: var(--neutral-800);
      margin-bottom: var(--space-2);
      line-height: 1.8;
    }
  }

  ol {
    margin: var(--space-4) 0;
    padding-left: var(--space-6);

    li {
      font-size: var(--text-base);
      color: var(--neutral-800);
      margin-bottom: var(--space-2);
      line-height: 1.8;
    }
  }
`;

const ContactBox = styled.div`
  background: var(--neutral-100);
  border: 1px solid var(--neutral-200);
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  margin: var(--space-8) 0;

  h3 {
    font-size: var(--text-lg);
    color: var(--neutral-900);
    margin-bottom: var(--space-3);
  }

  p {
    font-size: var(--text-base);
    color: var(--neutral-700);
    margin: 0;

    a {
      color: var(--primary-600);
      font-weight: 500;
    }
  }
`;

const TermsPage = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <PageTitle>Terms of Service</PageTitle>
          <PageSubtitle>
            Please read these terms carefully before using our website and services.
          </PageSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <LastUpdated>Last updated: December 2024</LastUpdated>

        <ContentBlock>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using intellica.it.com ("the Website," "we," "us," or "our"), you accept 
            and agree to be bound by these Terms of Service and all applicable laws and regulations. 
            If you do not agree with any of these terms, you are prohibited from using or accessing this site.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>2. Use License</h2>
          <p>
            Permission is granted to temporarily download one copy of the materials on Intellica for 
            personal, non-commercial transitory viewing only. This is the grant of a license, not a 
            transfer of title, and under this license you may not:
          </p>
          <ul>
            <li>Modify or copy the materials</li>
            <li>Use the materials for any commercial purpose or for any public display</li>
            <li>Attempt to reverse engineer any software contained on the Website</li>
            <li>Remove any copyright or other proprietary notations from the materials</li>
            <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
          </ul>
          <p>
            This license shall automatically terminate if you violate any of these restrictions and may 
            be terminated by us at any time. Upon terminating your viewing of these materials or upon 
            the termination of this license, you must destroy any downloaded materials in your possession.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>3. Content and Intellectual Property</h2>
          <p>
            All content published on this Website, including but not limited to articles, images, graphics, 
            logos, and text, is the property of Intellica or its content suppliers and is protected by 
            international copyright laws.
          </p>
          <p>
            You may not reproduce, distribute, modify, create derivative works of, publicly display, 
            publicly perform, republish, download, store, or transmit any of the material on our Website, 
            except as follows:
          </p>
          <ul>
            <li>Your computer may temporarily store copies of such materials incidental to your accessing and viewing</li>
            <li>You may store files that are automatically cached by your web browser</li>
            <li>You may print or download one copy of pages for your personal, non-commercial use</li>
            <li>You may share links to our content, provided you give appropriate credit</li>
          </ul>
        </ContentBlock>

        <ContentBlock>
          <h2>4. User Conduct</h2>
          <p>
            When using our Website, you agree not to:
          </p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe upon the rights of others</li>
            <li>Transmit any harmful, offensive, or disruptive content</li>
            <li>Attempt to gain unauthorized access to any portion of the Website</li>
            <li>Interfere with the proper functioning of the Website</li>
            <li>Use automated systems or software to extract data from the Website</li>
            <li>Engage in any activity that could damage, disable, or impair the Website</li>
            <li>Use the Website for any fraudulent or unlawful purpose</li>
          </ul>
        </ContentBlock>

        <ContentBlock>
          <h2>5. User-Generated Content</h2>
          <p>
            If you submit comments, feedback, suggestions, or other content to us, you grant us a 
            non-exclusive, worldwide, royalty-free, perpetual, and irrevocable right to use, reproduce, 
            modify, adapt, publish, distribute, and display such content in any media.
          </p>
          <p>
            You represent and warrant that:
          </p>
          <ul>
            <li>You own or control all rights to the content you submit</li>
            <li>Your content does not infringe any third-party rights</li>
            <li>Your content is accurate and not misleading</li>
            <li>Your content does not violate any applicable laws or regulations</li>
          </ul>
        </ContentBlock>

        <ContentBlock>
          <h2>6. Disclaimer of Warranties</h2>
          <p>
            The materials on Intellica's website are provided on an 'as is' basis. Intellica makes no 
            warranties, expressed or implied, and hereby disclaims and negates all other warranties 
            including, without limitation, implied warranties or conditions of merchantability, fitness 
            for a particular purpose, or non-infringement of intellectual property.
          </p>
          <p>
            We do not warrant that:
          </p>
          <ul>
            <li>The Website will operate uninterrupted or error-free</li>
            <li>The Website or its servers are free of viruses or harmful components</li>
            <li>The information on the Website is accurate, complete, or current</li>
            <li>The results obtained from use of the Website will meet your requirements</li>
          </ul>
        </ContentBlock>

        <ContentBlock>
          <h2>7. Limitation of Liability</h2>
          <p>
            In no event shall Intellica or its suppliers be liable for any damages (including, without 
            limitation, damages for loss of data or profit, or due to business interruption) arising out 
            of the use or inability to use the materials on Intellica's website, even if Intellica or an 
            authorized representative has been notified orally or in writing of the possibility of such damage.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>8. Third-Party Links and Content</h2>
          <p>
            Our Website may contain links to third-party websites or services that are not owned or 
            controlled by Intellica. We have no control over, and assume no responsibility for, the 
            content, privacy policies, or practices of any third-party websites or services.
          </p>
          <p>
            You acknowledge and agree that Intellica shall not be responsible or liable for any damage 
            or loss caused by your use of any third-party content or services.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>9. Privacy Policy</h2>
          <p>
            Your use of our Website is also governed by our Privacy Policy. Please review our Privacy 
            Policy, which also governs the site and informs users of our data collection practices.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>10. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless Intellica and its officers, directors, 
            employees, agents, and affiliates from and against any claims, damages, obligations, losses, 
            liabilities, costs, or debt, and expenses (including attorney's fees) arising from:
          </p>
          <ul>
            <li>Your use of and access to the Website</li>
            <li>Your violation of any term of these Terms of Service</li>
            <li>Your violation of any third-party right</li>
            <li>Any content you submit to the Website</li>
          </ul>
        </ContentBlock>

        <ContentBlock>
          <h2>11. Termination</h2>
          <p>
            We may terminate or suspend your access to our Website immediately, without prior notice or 
            liability, for any reason whatsoever, including without limitation if you breach these Terms 
            of Service.
          </p>
          <p>
            Upon termination, your right to use the Website will cease immediately. All provisions of 
            these Terms which by their nature should survive termination shall survive termination.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>12. Governing Law</h2>
          <p>
            These Terms shall be governed and construed in accordance with the laws of the jurisdiction 
            in which Intellica operates, without regard to its conflict of law provisions. You agree to 
            submit to the personal jurisdiction of the courts located within this jurisdiction.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>13. Changes to Terms</h2>
          <p>
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
            If a revision is material, we will provide notice prior to any new terms taking effect by 
            posting the new terms on this page and updating the "Last updated" date.
          </p>
          <p>
            By continuing to access or use our Website after those revisions become effective, you agree 
            to be bound by the revised terms.
          </p>
        </ContentBlock>

        <ContentBlock>
          <h2>14. Severability</h2>
          <p>
            If any provision of these Terms is held to be unenforceable or invalid, such provision will 
            be changed and interpreted to accomplish the objectives of such provision to the greatest 
            extent possible under applicable law, and the remaining provisions will continue in full 
            force and effect.
          </p>
        </ContentBlock>

        <ContactBox>
          <h3>Contact Information</h3>
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <p>
            Email: <a href="mailto:legal@intellica.it.com">legal@intellica.it.com</a>
          </p>
          <p>
            Address: Intellica.it.com<br />
            Legal Department<br />
            Terms of Service Inquiries
          </p>
        </ContactBox>

        <ContentBlock>
          <p>
            By using our Website, you acknowledge that you have read, understood, and agree to be bound 
            by these Terms of Service.
          </p>
        </ContentBlock>
      </ContentSection>
    </PageContainer>
  );
};

export default TermsPage;